/**
 * printRouteSheet.js
 * Generates a printable route sheet for a messenger's active route.
 * Opens a new window with a formatted table ready for printing.
 */

const fmt = (val) => {
    const num = parseFloat(val || 0);
    return new Intl.NumberFormat('es-CO', {
        style: 'currency',
        currency: 'COP',
        maximumFractionDigits: 0
    }).format(num);
};

const fmtDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    // Adjust for Colombia timezone (UTC-5)
    d.setHours(d.getHours() - 5);
    return d.toLocaleDateString('es-CO', { day: '2-digit', month: '2-digit', year: '2-digit' });
};

export const printRouteSheet = (route) => {
    if (!route || !route.orders) return;

    const orders = route.orders || [];
    const additionalStops = (route.additionalStops || []).filter(s => s.status === 'pending' || s.status === 'completed');

    // Stats
    const siigoOrders = orders.filter(o => !String(o.orderNumber || o.order_number || '').startsWith('INT-'));

    let intCount = orders.filter(o => String(o.orderNumber || o.order_number || '').startsWith('INT-')).length;
    siigoOrders.forEach(o => {
        if (o.internal_orders && o.internal_orders.length > 0) {
            intCount += o.internal_orders.length;
        } else if (o.int_order_number || o.internal_order_numbers) {
            const nums = String(o.internal_order_numbers || o.int_order_number || '').split(',').filter(n => n.trim().startsWith('INT-'));
            intCount += Math.max(0, nums.length);
        }
    });

    const totalSiigo = siigoOrders.length;
    const totalAdditional = additionalStops.length;
    const totalStops = totalSiigo + totalAdditional;
    const totalOrders = totalSiigo + intCount;

    // Financial calculations
    let paidCount = 0;
    let pendingCount = 0;
    let valSiigo = 0;
    let valInt = 0;
    let totalPaid = 0;
    let totalBalance = 0;

    orders.forEach(o => {
        const isInt = String(o.orderNumber || o.order_number || '').startsWith('INT-');
        const mainAmount = parseFloat(o.totalAmount || o.total_amount || 0);
        const childAmount = parseFloat(o.int_amount || 0);
        const total = mainAmount + childAmount;
        const balance = parseFloat(o.calculated_balance_due ?? (total - parseFloat(o.paid_amount || o.totalPaid || 0)));

        if (isInt) {
            valInt += mainAmount;
        } else {
            valSiigo += mainAmount;
            valInt += childAmount;
        }

        totalPaid += (total - Math.max(0, balance));
        totalBalance += Math.max(0, balance);

        if (!isInt) {
            if (balance <= 0) paidCount++;
            else pendingCount++;
        }
    });

    const totalValue = valSiigo + valInt;

    const routeDate = route.routeDate
        ? new Date(route.routeDate).toLocaleDateString('es-CO', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })
        : '-';

    // Build Rows
    const rows = [];
    let stopIdx = 1;

    siigoOrders.forEach((o) => {
        const total = parseFloat(o.totalAmount || o.total_amount || 0);
        const balance = Math.max(0, parseFloat(o.calculated_balance_due ?? (total - parseFloat(o.paid_amount || o.totalPaid || 0))));
        const paid = total - balance;
        const isPaid = balance <= 0;
        const isCredit = String(o.paymentMethod || o.payment_method || '').toLowerCase().includes('cred');
        const orderDate = fmtDate(o.createdAt || o.shipping_date || o.created_at);

        // Parent Siigo Row (Inc Stop index)
        const currentStop = stopIdx++;
        rows.push(`
            <tr>
                <td style="text-align:center;font-weight:bold;color:#333;font-size:12px;background:#f8fafc;">${currentStop}</td>
                <td style="font-weight:bold;white-space:nowrap;">${o.orderNumber || o.order_number || '-'}</td>
                <td style="font-weight:600;">${o.customerName || o.customer_name || '-'}</td>
                <td style="font-size:10px;">${o.deliveryAddress || o.delivery_address || '-'}${o.deliveryCity || o.delivery_city ? `, ${o.deliveryCity || o.delivery_city}` : ''}</td>
                <td style="white-space:nowrap;">${o.customerPhone || o.customer_phone || '-'}</td>
                <td style="text-align:center;font-size:10px;">${orderDate}</td>
                <td style="text-align:right;font-weight:bold;">${fmt(total)}</td>
                <td style="text-align:right;color:#16a34a;">${paid > 0 ? fmt(paid) : '-'}</td>
                <td style="text-align:right;font-weight:bold;color:${balance > 0 ? '#ea580c' : '#16a34a'};">
                    ${balance > 0 ? fmt(balance) : '✓'}
                    ${isCredit ? '<br><span style="font-size:9px;color:#2563eb;">CRÉDITO</span>' : ''}
                </td>
                <td style="text-align:center;">
                    ${isPaid ? '<span style="color:#16a34a;font-weight:bold;">✓</span>' : '<span style="color:#ea580c;">◻</span>'}
                </td>
            </tr>
        `);

        // Child INT rows (SHARE Stop index/context)
        const children = [];
        if (o.internal_orders && o.internal_orders.length > 0) {
            o.internal_orders.forEach(c => children.push({
                num: c.orderNumber || c.order_number,
                val: c.totalAmount || c.total_amount || 0
            }));
        } else if (o.int_order_number || o.internal_order_numbers) {
            const nums = String(o.internal_order_numbers || o.int_order_number || '').split(',');
            const amounts = String(o.int_amount || '').split(',');
            nums.forEach((num, i) => {
                if (num.trim()) {
                    children.push({
                        num: num.trim(),
                        val: parseFloat(amounts[i] || o.int_amount || 0)
                    });
                }
            });
        }

        children.forEach(child => {
            rows.push(`
                <tr style="background:#f1f5f9;border-left:4px solid #3b82f6;">
                    <td style="text-align:center;color:#64748b;font-weight:bold;">↳</td>
                    <td style="color:#2563eb;font-weight:bold;">${child.num}</td>
                    <td colspan="4" style="color:#2563eb;font-style:italic;font-size:9px;">
                        Pedido Interno (Misma parada #${currentStop})
                    </td>
                    <td style="text-align:right;font-weight:bold;color:#2563eb;">${fmt(child.val)}</td>
                    <td style="text-align:right;color:#16a34a;">-</td>
                    <td style="text-align:right;font-weight:bold;color:#16a34a;">✓</td>
                    <td style="text-align:center;color:#16a34a;font-weight:bold;">✓</td>
                </tr>
            `);
        });
    });

    // Additional stops (Inc Stop index)
    const stopRows = additionalStops.length > 0 ? `
        <tr>
            <td colspan="10" style="background:#f0f0ff;font-weight:bold;padding:8px;font-size:11px;color:#6366f1;border-top:2px solid #6366f1;">
                PARADAS ADICIONALES (${additionalStops.length})
            </td>
        </tr>
        ${additionalStops.map((s) => `
            <tr style="background:#fafaff;">
                <td style="text-align:center;color:#333;font-weight:bold;font-size:12px;background:#f5f3ff;">${stopIdx++}</td>
                <td style="color:#6366f1;font-weight:bold;">${s.stopType === 'pickup' ? '🔄 Recogida' : s.stopType === 'delivery' ? '📦 Entrega' : '📍 Parada'}</td>
                <td colspan="2">${s.contactName || '-'}</td>
                <td colspan="2" style="font-size:10px;">${s.address || '-'}</td>
                <td colspan="2">${s.contactPhone || '-'}</td>
                <td style="font-size:10px;">${s.notes || ''}</td>
                <td style="text-align:center;">${s.status === 'completed' ? '<span style="color:#16a34a;">✓</span>' : '◻'}</td>
            </tr>
        `).join('')}
    ` : '';

    const orderRows = rows.join('');

    const html = `
<!DOCTYPE html>
<html>
<head>
    <title>Hoja de Ruta #${route.routeNumber} - ${route.messengerName}</title>
    <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: Arial, Helvetica, sans-serif; padding: 15px; font-size: 11px; color: #333; }
        
        .header { 
            border-bottom: 3px solid #1e40af; 
            padding-bottom: 10px; 
            margin-bottom: 12px; 
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
        }
        .header-left h1 { font-size: 18px; color: #1e40af; margin-bottom: 2px; }
        .header-left p { font-size: 11px; color: #666; }
        .header-right { text-align: right; }
        .header-right .date { font-size: 13px; font-weight: bold; }
        .header-right .messenger { font-size: 14px; font-weight: bold; color: #1e40af; }

        .summary { 
            display: flex; 
            gap: 8px; 
            margin-bottom: 12px; 
            flex-wrap: wrap;
        }
        .stat { 
            flex: 1; 
            min-width: 85px;
            border: 1px solid #ddd; 
            padding: 6px 10px; 
            border-radius: 4px; 
            text-align: center;
        }
        .stat .label { font-size: 8px; text-transform: uppercase; font-weight: bold; color: #888; letter-spacing: 0.5px; }
        .stat .value { font-size: 15px; font-weight: 900; }
        .stat.green .value { color: #16a34a; }
        .stat.orange .value { color: #ea580c; }
        .stat.blue .value { color: #1e40af; }

        table { width: 100%; border-collapse: collapse; margin-bottom: 10px; }
        th { 
            background: #1e40af; 
            color: white; 
            padding: 6px 8px; 
            font-size: 9px; 
            text-transform: uppercase; 
            letter-spacing: 0.5px;
            white-space: nowrap;
        }
        td { 
            padding: 5px 8px; 
            border-bottom: 1px solid #e5e5e5; 
            font-size: 10px;
            vertical-align: top;
        }
        tr:nth-child(even) { background: #fafafa; }
        tr:hover { background: #f0f7ff; }

        .totals { 
            border-top: 2px solid #1e40af; 
            padding-top: 8px; 
            display: flex;
            justify-content: space-between;
            font-size: 12px;
        }
        .totals .item { text-align: center; }
        .totals .item .label { font-size: 9px; color: #888; text-transform: uppercase; }
        .totals .item .value { font-size: 16px; font-weight: 900; }

        .footer { 
            margin-top: 20px; 
            padding-top: 10px; 
            border-top: 1px dashed #ccc; 
            display: flex; 
            justify-content: space-between;
            font-size: 10px;
            color: #999;
        }
        .signature { 
            margin-top: 30px; 
            display: flex; 
            justify-content: space-around; 
        }
        .sig-line { 
            width: 200px; 
            text-align: center; 
            padding-top: 30px; 
            border-top: 1px solid #333; 
            font-size: 10px; 
            color: #666; 
        }

        @media print {
            body { padding: 10px; }
            tr:hover { background: inherit; }
            @page { margin: 8mm; size: landscape; }
        }
    </style>
</head>
<body>
    <div class="header">
        <div class="header-left">
            <h1>🚚 Hoja de Ruta #${route.routeNumber}</h1>
            <p>Topping Frozen — Control de Despacho</p>
        </div>
        <div class="header-right">
            <div class="messenger">${route.messengerName || 'Mensajero'}</div>
            <div class="date">${routeDate}</div>
            ${route.lockedAt ? `<div style="font-size:10px;color:#666;margin-top:2px;">Consolidada: ${new Date(route.lockedAt).toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' })}</div>` : ''}
        </div>
    </div>

    <div class="summary">
        <div class="stat blue" style="background:#f0f9ff; border-color:#bae6fd;">
            <div class="label">TOTAL PARADAS</div>
            <div class="value">${totalStops}</div>
        </div>
        <div class="stat">
            <div class="label">Paradas SIIGO</div>
            <div class="value">${totalSiigo}</div>
        </div>
        <div class="stat" style="border-color:#2563eb; background:#f0f7ff;">
            <div class="label" style="color:#2563eb;">Pedidos INT</div>
            <div class="value" style="color:#2563eb;">${intCount}</div>
        </div>
        <div class="stat" style="border-color:#6366f1;">
            <div class="label">Paradas Adic.</div>
            <div class="value" style="color:#6366f1;">${totalAdditional}</div>
        </div>
        <div class="stat green">
            <div class="label">Pagos ✓</div>
            <div class="value">${paidCount}</div>
        </div>
        <div class="stat orange">
            <div class="label">Pendientes</div>
            <div class="value">${pendingCount}</div>
        </div>
        <div class="stat">
            <div class="label">Total Valor</div>
            <div class="value">${fmt(totalValue)}</div>
        </div>
        <div class="stat orange">
            <div class="label">Saldo a Cobrar</div>
            <div class="value">${fmt(totalBalance)}</div>
        </div>
    </div>

    <table>
        <thead>
            <tr>
                <th style="width:35px;">#</th>
                <th>Pedido</th>
                <th>Cliente</th>
                <th>Dirección</th>
                <th>Contacto</th>
                <th>F. Pedido</th>
                <th style="text-align:right;">Valor</th>
                <th style="text-align:right;">Pagado</th>
                <th style="text-align:right;">Saldo</th>
                <th style="text-align:center;width:30px;">✓</th>
            </tr>
        </thead>
        <tbody>
            ${orderRows}
            ${stopRows}
        </tbody>
    </table>

    <div class="totals" style="border-top: 2px solid #1e40af; background:#f8fafc; padding: 12px;">
        <div class="item">
            <div class="label">Total Paradas</div>
            <div class="value">${totalStops}</div>
        </div>
        <div class="item">
            <div class="label">Total Órdenes</div>
            <div class="value">${totalOrders}</div>
        </div>
        <div class="item" style="border-left:1px solid #ddd; padding-left:15px;">
            <div class="label">Valor SIIGO</div>
            <div class="value" style="color:#1e40af;">${fmt(valSiigo)}</div>
        </div>
        <div class="item">
            <div class="label">Valor INT</div>
            <div class="value" style="color:#2563eb;">${fmt(valInt)}</div>
        </div>
        <div class="item" style="border-left:2px solid #1e40af; padding-left:15px;">
            <div class="label">Valor TOTAL</div>
            <div class="value">${fmt(totalValue)}</div>
        </div>
        <div class="item">
            <div class="label">Por Cobrar</div>
            <div class="value" style="color:#ea580c;">${fmt(totalBalance)}</div>
        </div>
    </div>

    <div class="signature">
        <div class="sig-line">Despachado por</div>
        <div class="sig-line">Recibido por (Mensajero)</div>
    </div>

    <div class="footer">
        <span>Impreso: ${new Date().toLocaleString('es-CO')}</span>
        <span>Ruta #${route.routeNumber} • ${route.messengerName}</span>
    </div>

    <script>window.onload = () => window.print();</script>
</body>
</html>
    `;

    const printWindow = window.open('', '_blank', 'width=1100,height=800');
    if (printWindow) {
        printWindow.document.write(html);
        printWindow.document.close();
    }
};

/**
 * Print route sheet from the dispatch view data shape.
 * Normalizes the dispatch-format messenger object into the shape expected by printRouteSheet.
 * @param {Object} messenger - messenger object from dispatch-overview API (snake_case fields)
 * @param {string} dateStr - route date string (YYYY-MM-DD)
 */
export const printDispatchRouteSheet = (messenger, dateStr) => {
    if (!messenger || !messenger.orders) return;

    // Normalize to the shape expected by printRouteSheet
    const normalizedRoute = {
        routeNumber: messenger.route_number || messenger.route_id || '-',
        routeDate: dateStr,
        messengerName: messenger.messenger_name,
        totalOrders: messenger.total_orders,
        lockedAt: messenger.locked_at,
        orders: (messenger.orders || []).map(o => ({
            order_id: o.id,
            orderNumber: o.order_number,
            customerName: o.customer_name,
            customerPhone: o.customer_phone,
            deliveryAddress: o.customer_address || o.delivery_address,
            deliveryCity: o.customer_city || o.delivery_city,
            totalAmount: o.total_amount,
            totalPaid: o.total_paid,
            paid_amount: o.paid_amount,
            calculated_balance_due: o.calculated_balance_due,
            paymentMethod: o.payment_method,
            shipping_date: o.shipping_date,
            createdAt: o.created_at,
            int_amount: o.int_amount || o.internal_orders_total || 0,
            int_order_number: o.int_order_number,
            internal_order_numbers: o.internal_order_numbers,
            internal_orders: o.internal_orders || []
        })),
        additionalStops: (messenger.additional_stops || []).map(s => ({
            stopType: s.stop_type,
            address: s.address,
            contactName: s.contact_name,
            contactPhone: s.contact_phone,
            notes: s.description || s.notes,
            status: s.status,
        })),
    };

    printRouteSheet(normalizedRoute);
};
